import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useWallet, useConnection } from '@solana/wallet-adapter-react'
import {
  Users,
  Vote,
  TrendingUp,
  ArrowRight,
  Search,
  DollarSign,
  Shield,
  CheckCircle2,
  Target,
  Github,
  ExternalLink,
  Info,
  Plus,
  Zap,
  AlertCircle,
  Loader2,
  Twitter,
  Send,
  Coins,
  Percent
} from 'lucide-react'
import { createCtoPoolsClient, lamportsToSol } from '../lib/anchorClient'
import { HowItWorksInfographic } from '../components/HowItWorksInfographic'
import WalletConnectButton from '../components/WalletConnectButton'
import { useJitoAPY } from '../hooks/useJitoAPY'

/**
 * Interface for platform statistics
 * Defines structure for real-time blockchain metrics
 */
interface PlatformStats {
  activePools: number
  totalSolLocked: number
  communityMembers: number
}

/**
 * Home Page Component with cyberpunk theme
 *
 * Main landing page for CTO Pools platform featuring:
 * - Hero section with wallet connection status and V2 liquid staking benefits
 * - Key features showcase including yield earning
 * - How it works process flow with Jito integration
 * - Pool search functionality
 * - Platform statistics with real blockchain data
 * - Call-to-action sections
 *
 * Note (2026-01-18): Updated to use WalletConnectButton which shows TOS before wallet modal
 *
 * Accessibility: WCAG 2.1 AA compliant
 * Responsive: Mobile-first design with breakpoints at sm/md/lg/xl
 */
export function Home(): JSX.Element {
  const { formattedApy } = useJitoAPY()
  const navigate = useNavigate()
  const wallet = useWallet()
  const { connected, publicKey } = wallet
  const { connection } = useConnection()
  const [searchTerm, setSearchTerm] = useState('')
  
  // Statistics state
  const [stats, setStats] = useState<PlatformStats>({
    activePools: 0,
    totalSolLocked: 0,
    communityMembers: 0
  })
  const [isLoadingStats, setIsLoadingStats] = useState(true)
  const [statsError, setStatsError] = useState<string>('')

  /**
   * Handle search form submission
   * Redirects to pools page with search query
   */
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (searchTerm.trim()) {
      // Navigate to pools page with search term
      navigate(`/pools?search=${encodeURIComponent(searchTerm)}`)
    }
  }

  /**
   * Fetch platform statistics from blockchain
   * Queries all pools and calculates aggregate metrics
   */
  const fetchPlatformStats = async () => {
    try {
      setStatsError('')
      const client = createCtoPoolsClient(connection, wallet)
      
      // Fetch all pools
      const pools = await client.getAllPools()
      
      // Calculate active pools
      const activePools = pools.length
      
      // Calculate total SOL locked across all pools
      // V2: Uses totalPoolTokens (jitoSOL) instead of totalSolInPool
      const totalSolLocked = pools.reduce((total, pool) => {
        return total + lamportsToSol(pool.account.totalPoolTokens)
      }, 0)
      
      // Get unique community members (unique donors across all pools)
      const uniqueDonors = new Set<string>()
      
      // Fetch donors for each pool
      for (const pool of pools) {
        const donors = await client.getPoolDonors(pool.publicKey)
        donors.forEach(donor => {
          uniqueDonors.add(donor.account.wallet.toBase58())
        })
      }
      
      const communityMembers = uniqueDonors.size
      
      setStats({
        activePools,
        totalSolLocked,
        communityMembers
      })
    } catch (error) {
      console.error('Error fetching platform stats:', error)
      setStatsError('Unable to load statistics')
      // Keep existing stats on error
    } finally {
      setIsLoadingStats(false)
    }
  }

  /**
   * Load statistics on component mount and set up auto-refresh
   * Refreshes every 30 seconds to keep data current
   */
  useEffect(() => {
    fetchPlatformStats()
    
    // Set up auto-refresh every 30 seconds
    const intervalId = setInterval(() => {
      fetchPlatformStats()
    }, 30000) // 30 seconds
    
    // Cleanup interval on unmount
    return () => clearInterval(intervalId)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {/* Hero Section - Clean with Cyberpunk Theme */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          
          {/* Main Title */}
          <div className="space-y-6">
            <h1 className="text-5xl md:text-7xl font-bold text-gradient-cyber">
              CTO POOLS
            </h1>
            <p className="text-xl md:text-2xl text-text-secondary max-w-3xl mx-auto leading-relaxed">
              Community-driven funding platform for{' '}
              <span className="text-cyber-cyan font-semibold">abandoned token takeovers</span>{' '}
              on Solana
            </p>
            {/* V2 Liquid Staking Badge */}
            <div className="inline-flex items-center glass-card border border-cyber-green/50 px-4 py-2 rounded-full">
              <Coins className="w-5 h-5 text-cyber-green mr-2" aria-hidden="true" />
              <span className="text-cyber-green font-semibold">V2: Earn {formattedApy} APY (current)</span>
              <span className="text-text-secondary ml-2">with Jito Liquid Staking</span>
            </div>
          </div>

          {/* Wallet Status */}
          <div className="max-w-lg mx-auto">
            {connected && publicKey ? (
              <div className="glass-card border border-cyber-green/30 p-6">
                <div className="flex items-center justify-center space-x-3 mb-3">
                  <div className="w-3 h-3 bg-cyber-green rounded-full animate-pulse" />
                  <span className="text-cyber-green font-semibold">Wallet Connected</span>
                </div>
                <div className="text-text-secondary font-mono text-sm">
                  {publicKey.toBase58().slice(0, 8)}...{publicKey.toBase58().slice(-8)}
                </div>
              </div>
            ) : (
              <div className="glass-card border border-cyber-yellow/30 p-6">
                <div className="flex items-center justify-center space-x-3 mb-3">
                  <Shield className="w-6 h-6 text-cyber-yellow" />
                  <span className="text-cyber-yellow font-semibold">Connect Wallet to Start</span>
                </div>
                <p className="text-text-secondary">
                  Choose from Phantom, Backpack, Solflare, or other supported wallets
                </p>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            {connected ? (
              <button
                onClick={() => navigate('/pools')}
                className="cyber-button-primary flex items-center space-x-3 px-8 py-4"
              >
                <Users className="w-5 h-5" />
                <span>Explore Pools</span>
                <ArrowRight className="w-5 h-5" />
              </button>
            ) : (
              /* 
               * WalletConnectButton shows TOS modal BEFORE wallet selection
               * This replaces the WalletMultiButton for better TOS UX flow
               * Updated: 2026-01-18
               */
              <WalletConnectButton 
                connectLabel="Select Wallet"
                variant="primary"
                size="lg"
                className="!px-8 !py-4"
              />
            )}
            
            <button
              onClick={() => window.open('https://github.com/InventOfficial/CTO-Pools', '_blank')}
              className="cyber-button-secondary flex items-center space-x-3 px-8 py-4"
            >
              <Github className="w-5 h-5" />
              <span>Open Source</span>
              <ExternalLink className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* Features Section - Updated with V2 Liquid Staking */}
      <section className="py-16 px-4" aria-labelledby="features-heading">
        <div className="max-w-6xl mx-auto">
          
          {/* Section Header */}
          <div className="text-center mb-12">
            <h2 id="features-heading" className="text-4xl md:text-5xl font-bold text-gradient-cyber mb-6">
              Key Features
            </h2>
            <p className="text-xl text-text-secondary max-w-3xl mx-auto">
              Everything you need for transparent, community-driven token revivals with built-in yield generation
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Earn While Waiting - NEW V2 Feature */}
            <div className="glass-card border border-dark-border hover:border-cyber-green/50 p-8 transition-all duration-300">
              <div className="w-16 h-16 bg-cyber-green/20 rounded-lg mx-auto mb-6 flex items-center justify-center">
                <Percent className="w-8 h-8 text-cyber-green" aria-hidden="true" />
              </div>
              <h3 className="text-xl font-semibold text-cyber-green mb-4 text-center">
                Earn While Waiting
              </h3>
              <p className="text-text-secondary text-center leading-relaxed">
                Pool funds earn {formattedApy} APY through Jito liquid staking while waiting for proposals. Your donations work for the community.
              </p>
            </div>

            {/* Community Funding */}
            <div className="glass-card border border-dark-border hover:border-cyber-cyan/50 p-8 transition-all duration-300">
              <div className="w-16 h-16 bg-cyber-cyan/20 rounded-lg mx-auto mb-6 flex items-center justify-center">
                <Users className="w-8 h-8 text-cyber-cyan" aria-hidden="true" />
              </div>
              <h3 className="text-xl font-semibold text-cyber-cyan mb-4 text-center">
                Community Funding
              </h3>
              <p className="text-text-secondary text-center leading-relaxed">
                Pool SOL contributions from community members with transparent governance and proportional voting power
              </p>
            </div>

            {/* Democratic Governance */}
            <div className="glass-card border border-dark-border hover:border-cyber-purple/50 p-8 transition-all duration-300">
              <div className="w-16 h-16 bg-cyber-purple/20 rounded-lg mx-auto mb-6 flex items-center justify-center">
                <Vote className="w-8 h-8 text-cyber-purple" aria-hidden="true" />
              </div>
              <h3 className="text-xl font-semibold text-cyber-purple mb-4 text-center">
                Democratic Governance
              </h3>
              <p className="text-text-secondary text-center leading-relaxed">
                Decentralized decision-making through share-weighted voting on funding proposals and project direction
              </p>
            </div>

            {/* Token Revival */}
            <div className="glass-card border border-dark-border hover:border-cyber-magenta/50 p-8 transition-all duration-300">
              <div className="w-16 h-16 bg-cyber-magenta/20 rounded-lg mx-auto mb-6 flex items-center justify-center">
                <TrendingUp className="w-8 h-8 text-cyber-magenta" aria-hidden="true" />
              </div>
              <h3 className="text-xl font-semibold text-cyber-magenta mb-4 text-center">
                Token Revival
              </h3>
              <p className="text-text-secondary text-center leading-relaxed">
                Execute community-approved proposals to breathe new life into abandoned blockchain projects
              </p>
            </div>
          </div>

          {/* Note: The Jito Liquid Staking explanation banner was removed as it's redundant
              with the Token Revival Journey infographic in HowItWorksInfographic component */}
        </div>
      </section>

      {/* How It Works Section */}
      <HowItWorksInfographic />

      {/* Search Section */}
      <section className="py-16 px-4" aria-labelledby="search-heading">
        <div className="max-w-4xl mx-auto">
          
          <div className="text-center mb-12">
            <h2 id="search-heading" className="text-4xl md:text-5xl font-bold text-gradient-cyber mb-6">
              Find Community Pools
            </h2>
            <p className="text-xl text-text-secondary">
              Search for existing community takeover pools by token mint address
            </p>
          </div>

          <div className="glass-card border border-dark-border p-8">
            <form onSubmit={handleSearch} className="space-y-6">
              
              <div>
                <label htmlFor="search-input" className="block text-lg font-medium text-text-primary mb-4">
                  Token Mint Address
                </label>
                <div className="relative">
                  <input
                    id="search-input"
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Enter Solana token mint address..."
                    className="w-full pl-12 pr-32 py-4 bg-dark-surface border border-dark-border rounded-lg text-white placeholder-text-muted focus:border-cyber-cyan focus:ring-1 focus:ring-cyber-cyan focus:outline-none font-mono"
                    aria-label="Search for pool by token mint address"
                  />
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-text-muted" aria-hidden="true" />
                  <button
                    type="submit"
                    disabled={!searchTerm.trim()}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 cyber-button-primary disabled:opacity-50 disabled:cursor-not-allowed px-6 py-2"
                    aria-label="Search for pool"
                  >
                    Search
                  </button>
                </div>
              </div>

              {/* Example */}
              <div className="glass-card bg-cyber-cyan/10 border border-cyber-cyan/20 p-4">
                <div className="flex items-start space-x-3">
                  <Info className="w-5 h-5 text-cyber-cyan flex-shrink-0 mt-0.5" aria-hidden="true" />
                  <div>
                    <p className="text-text-secondary">
                      <span className="text-cyber-cyan font-semibold">Example:</span> A valid Solana token mint address 
                      looks like{' '}
                      <code className="bg-dark-panel px-2 py-1 rounded text-cyber-cyan font-mono text-sm">
                        So11111111111111111111111111111111111112
                      </code>
                    </p>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Platform Statistics Section */}
      <section className="py-16 px-4 bg-dark-surface/50" aria-labelledby="stats-heading">
        <div className="max-w-6xl mx-auto">
          
          <div className="text-center mb-12">
            <h2 id="stats-heading" className="text-4xl md:text-5xl font-bold text-gradient-success mb-6">
              Platform Statistics
            </h2>
            <p className="text-xl text-text-secondary">
              Real-time metrics from the Solana blockchain
            </p>
          </div>

          {/* Error Banner */}
          {statsError && (
            <div className="glass-card bg-status-error/10 border border-status-error/20 p-4 mb-8" role="alert">
              <div className="flex items-start space-x-3">
                <AlertCircle className="w-5 h-5 text-status-error flex-shrink-0 mt-0.5" aria-hidden="true" />
                <div>
                  <p className="text-text-secondary text-sm">
                    <span className="text-status-error font-semibold">Error:</span> {statsError}
                    {' '}Displaying cached data if available.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            
            {/* Active Pools */}
            <div
              className="glass-card border border-dark-border hover:border-cyber-cyan/50 p-6 text-center transition-all duration-300"
              role="region"
              aria-label="Active pools statistics"
            >
              <div className="w-12 h-12 bg-cyber-cyan/20 rounded-lg mx-auto mb-4 flex items-center justify-center">
                <Target className="w-6 h-6 text-cyber-cyan" aria-hidden="true" />
              </div>
              {isLoadingStats ? (
                <div className="text-3xl font-bold text-white mb-2 flex items-center justify-center">
                  <Loader2 className="w-8 h-8 animate-spin text-cyber-cyan" aria-hidden="true" />
                </div>
              ) : (
                <div className="text-3xl font-bold text-white mb-2">{stats.activePools}</div>
              )}
              <h4 className="text-cyber-cyan font-semibold mb-2">Active Pools</h4>
              <p className="text-text-secondary text-sm">Community pools currently active</p>
            </div>

            {/* SOL Locked */}
            <div
              className="glass-card border border-dark-border hover:border-cyber-green/50 p-6 text-center transition-all duration-300"
              role="region"
              aria-label="Total SOL locked statistics"
            >
              <div className="w-12 h-12 bg-cyber-green/20 rounded-lg mx-auto mb-4 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-cyber-green" aria-hidden="true" />
              </div>
              {isLoadingStats ? (
                <div className="text-3xl font-bold text-white mb-2 flex items-center justify-center">
                  <Loader2 className="w-8 h-8 animate-spin text-cyber-green" aria-hidden="true" />
                </div>
              ) : (
                <div className="text-3xl font-bold text-white mb-2">{stats.totalSolLocked.toFixed(2)}</div>
              )}
              <h4 className="text-cyber-green font-semibold mb-2">SOL Staked</h4>
              <p className="text-text-secondary text-sm">Total value staked via Jito, earning {formattedApy} APY (current)</p>
            </div>

            {/* Community Members */}
            <div
              className="glass-card border border-dark-border hover:border-cyber-purple/50 p-6 text-center transition-all duration-300"
              role="region"
              aria-label="Community members statistics"
            >
              <div className="w-12 h-12 bg-cyber-purple/20 rounded-lg mx-auto mb-4 flex items-center justify-center">
                <Users className="w-6 h-6 text-cyber-purple" aria-hidden="true" />
              </div>
              {isLoadingStats ? (
                <div className="text-3xl font-bold text-white mb-2 flex items-center justify-center">
                  <Loader2 className="w-8 h-8 animate-spin text-cyber-purple" aria-hidden="true" />
                </div>
              ) : (
                <div className="text-3xl font-bold text-white mb-2">{stats.communityMembers}</div>
              )}
              <h4 className="text-cyber-purple font-semibold mb-2">Community Members</h4>
              <p className="text-text-secondary text-sm">Unique contributors across all pools</p>
            </div>
          </div>

          {/* Auto-refresh indicator */}
          {!isLoadingStats && !statsError && (
            <div className="mt-6 text-center">
              {/* NOTE: Using <div> instead of <p> because we have a flex container with child elements.
                  Using <p> with <div> inside would cause DOM nesting violation */}
              <div className="text-xs text-text-muted flex items-center justify-center space-x-2">
                <span className="w-2 h-2 bg-cyber-green rounded-full animate-pulse" aria-hidden="true" />
                <span>Live data • Auto-refreshes every 30 seconds</span>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Community Section */}
      <section className="py-16 px-4" aria-labelledby="community-heading">
        <div className="max-w-4xl mx-auto text-center">
          <h2 id="community-heading" className="text-3xl md:text-4xl font-bold text-gradient-cyber mb-8">
            Join the Community
          </h2>
          <div className="flex justify-center gap-6">
            <a
              href="https://x.com/CtoPools"
              target="_blank"
              rel="noopener noreferrer"
              className="glass-card border border-dark-border hover:border-cyber-cyan p-6 flex flex-col items-center space-y-3 min-w-[160px] transition-all duration-300 group"
              aria-label="Follow CTO Pools on X (Twitter)"
            >
              <div className="w-12 h-12 bg-dark-surface rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                <Twitter className="w-6 h-6 text-cyber-cyan" aria-hidden="true" />
              </div>
              <span className="font-semibold text-text-primary">Follow on X</span>
            </a>
            <a
              href="https://t.me/ctopools"
              target="_blank"
              rel="noopener noreferrer"
              className="glass-card border border-dark-border hover:border-cyber-cyan p-6 flex flex-col items-center space-y-3 min-w-[160px] transition-all duration-300 group"
              aria-label="Join CTO Pools Telegram community"
            >
              <div className="w-12 h-12 bg-dark-surface rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                <Send className="w-6 h-6 text-cyber-cyan" aria-hidden="true" />
              </div>
              <span className="font-semibold text-text-primary">Join Telegram</span>
            </a>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 px-4" aria-labelledby="cta-heading">
        <div className="max-w-4xl mx-auto text-center">
          
          <div className="glass-card border border-dark-border p-12 space-y-8">
            <div className="space-y-6">
              <h2 id="cta-heading" className="text-4xl md:text-5xl font-bold text-gradient-premium">
                Ready to Revive Abandoned Tokens?
              </h2>
              <p className="text-xl text-text-secondary leading-relaxed">
                Join the movement to bring forgotten projects back to life through{' '}
                <span className="text-cyber-cyan font-semibold">community power</span>,{' '}
                transparent governance, and{' '}
                <span className="text-cyber-green font-semibold">yield-generating pools</span>.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <button 
                onClick={() => navigate('/pools')}
                className="cyber-button-primary flex items-center space-x-3 px-10 py-4"
                aria-label="Browse existing pools"
              >
                <Users className="w-5 h-5" aria-hidden="true" />
                <span>Browse Pools</span>
                <ArrowRight className="w-5 h-5" aria-hidden="true" />
              </button>
              
              <button 
                onClick={() => navigate('/create-pool')}
                className="cyber-button-secondary flex items-center space-x-3 px-10 py-4"
                aria-label="Create a new pool"
              >
                <Plus className="w-5 h-5" aria-hidden="true" />
                <span>Create Pool</span>
              </button>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-text-secondary pt-6 border-t border-dark-border">
              <div className="flex items-center space-x-2">
                <Shield className="w-4 h-4 text-cyber-green" aria-hidden="true" />
                <span>Secure & Transparent</span>
              </div>
              <div className="flex items-center space-x-2">
                <Coins className="w-4 h-4 text-cyber-green" aria-hidden="true" />
                <span>Earn {formattedApy} APY via Jito</span>
              </div>
              <div className="flex items-center space-x-2">
                <Zap className="w-4 h-4 text-cyber-yellow" aria-hidden="true" />
                <span>Lightning Fast</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle2 className="w-4 h-4 text-cyber-cyan" aria-hidden="true" />
                <span>Community Driven</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default Home
